/**
 * KAN-618: профиль аккаунта из Telegram в ячейке аккаунта: аватар, имя,
 * @username. Без профиля: инициалы (только буквы) или иконка.
 * KAN-623: под профилем кнопки-ссылки "Обновить" и "Изменить профиль".
 * KAN-648: кнопка-ссылка "Видео и 2FA" (ссылка, файл видео, признак 2FA).
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Film, Pencil, RefreshCw, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAvatarSrc } from '@/hooks/useAvatarSrc';
import { profileInitials } from '@/lib/telegramProfile';
import { AccountVideoDialog } from '@/components/telegram/AccountVideoDialog';
import { EditProfileDialog } from '@/components/telegram/EditProfileDialog';
import { describeProfileRefreshError } from '@/lib/telegramConnect';
import { PERSONAL_PROFILE_LOCKED, isPersonalTelegramAccount } from '@/lib/telegramPersonalAccount';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const QUERY_KEY = ['telegram-accounts'];
const REFRESH_LABEL = 'Обновить профиль';
const EDIT_LABEL = 'Изменить профиль';
const REFRESH_DONE = 'Профиль обновлён';
const REFRESH_ERROR = 'Не удалось обновить профиль';

const LINK_BUTTON =
  'inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline';

function updatedTitle(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return `обновлено ${date.toLocaleString('ru-RU')}`;
}

function Avatar({
  account,
  initials,
}: {
  account: TelegramAccount;
  initials: string;
}): JSX.Element {
  const url = account.avatar_url ?? '';
  const src = useAvatarSrc(url, account.profile_updated_at ?? '');
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const key = `${account.operator_id}-${account.account_id}`;

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt=""
        data-testid={`profile-avatar-${key}`}
        className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
        onError={() => setFailedSrc(src)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      data-testid={`profile-initials-${key}`}
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600"
    >
      {initials || <User className="h-4 w-4" />}
    </span>
  );
}

export function AccountProfile({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;
  const name = (account.profile_name ?? '').trim();
  const isPrimary = account.account_id === 'primary';
  const canManage = !isPrimary && !account.decommissioned;
  // KAN-871: профиль личного аккаунта менеджера меняет сам менеджер (backend 409).
  const personal = isPersonalTelegramAccount(account);
  const editBlocked = account.can_edit_profile === false || personal;
  const [editOpen, setEditOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const refresh = useMutation({
    mutationFn: () =>
      telegramAccountsApi.refreshProfile(
        account.account_id,
        account.operator_id
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(REFRESH_DONE);
    },
    onError: (err) =>
      toast.error(describeProfileRefreshError(err, REFRESH_ERROR)),
  });

  return (
    <div className="mb-1">
      <div
        data-testid={`profile-${key}`}
        title={updatedTitle(account.profile_updated_at)}
        className="flex items-center gap-2"
      >
        <Avatar
          account={account}
          initials={profileInitials(name || account.username, account.label)}
        />
        {(name || account.username) && (
          <div className="flex min-w-0 flex-col">
            {name && (
              <span
                data-testid={`profile-name-${key}`}
                className="truncate text-sm font-semibold text-gray-900"
              >
                {name}
              </span>
            )}
            {account.username && (
              <a
                href={`https://t.me/${encodeURIComponent(account.username)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-xs text-gray-500 hover:underline"
              >
                @{account.username}
              </a>
            )}
          </div>
        )}
      </div>
      {!isPrimary && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {canManage && (
            <button
              type="button"
              aria-label={REFRESH_LABEL}
              title={REFRESH_LABEL}
              disabled={refresh.isPending}
              data-testid={`refresh-profile-${key}`}
              className={LINK_BUTTON}
              onClick={() => refresh.mutate()}
            >
              <RefreshCw
                className={`h-3 w-3 ${refresh.isPending ? 'animate-spin' : ''}`}
              />
              Обновить
            </button>
          )}
          {canManage && (
            <button
              type="button"
              title={
                personal
                  ? PERSONAL_PROFILE_LOCKED
                  : editBlocked
                    ? account.profile_edit_block_reason ||
                      'Менять профиль сейчас нельзя'
                    : EDIT_LABEL
              }
              disabled={editBlocked}
              data-testid={`edit-profile-${key}`}
              className={LINK_BUTTON}
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-3 w-3" />
              {EDIT_LABEL}
            </button>
          )}
          <button
            type="button"
            title="Видеозапись аккаунта и признак 2FA"
            data-testid={`video-edit-${key}`}
            className={LINK_BUTTON}
            onClick={() => setVideoOpen(true)}
          >
            <Film className="h-3 w-3" />
            Видео и 2FA
          </button>
        </div>
      )}
      {!isPrimary && (
        <AccountVideoDialog
          account={account}
          open={videoOpen}
          onOpenChange={setVideoOpen}
        />
      )}
      {canManage && (
        <EditProfileDialog
          account={account}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </div>
  );
}
